// SCP-2053: SmartyStreets address verification for storefront checkouts.
// Renders/drives the shared modal from app/views/addresses/_verification_modal.html.erb.
//
// Usage (from a storefront checkout view):
//   AddressVerification.check({
//     fieldName: function (field) { return "shipping_address[" + field + "]"; },
//     label: 'Shipping',                 // used in the modal title/copy
//     onProceed: function () { $('#payment-form').get(0).submit(); }
//   });
//
// Use checkBilling() rather than check() for billing addresses: it skips pages where
// the billing form isn't actually being filled in. Do NOT gate that on the
// "same as shipping" checkbox - see billingFormInUse() below.
//
// `fieldName` maps a logical field (line_one/line_two/city/state/zip_code/country) to
// the actual `name` attribute used on the page - this varies between storefronts (e.g.
// flat "shipping_address[line_one]" vs Rails nested "order[shipping_address_attributes][line_one]").
//
// onProceed is called once the address is confirmed valid (or verification is
// bypassed/unavailable) - it should perform the actual form submission.

(function () {
  'use strict';

  var FIELDS = ['line_one', 'line_two', 'city', 'state', 'zip_code'];

  function fieldSelector(fieldName, field) {
    return '[name="' + fieldName(field) + '"]';
  }

  // A fieldName mapping that matches nothing looks exactly like an empty form, and
  // every path from there proceeds with the order. Say so rather than failing silently.
  function warn(message) {
    if (window.console && window.console.warn) {
      window.console.warn('AddressVerification: ' + message);
    }
  }

  function readAddress(fieldName) {
    var address = { country: $(fieldSelector(fieldName, 'country')).val() || 'US' };
    FIELDS.forEach(function (field) {
      address[field] = $(fieldSelector(fieldName, field)).val();
    });
    return address;
  }

  function writeAddress(fieldName, address) {
    FIELDS.forEach(function (field) {
      $(fieldSelector(fieldName, field)).val(address[field] || '');
    });
  }

  function isPopulated(address) {
    return FIELDS.some(function (field) {
      return $.trim(address[field] || '') !== '';
    });
  }

  // Whether a billing address is actually being entered on this page.
  //
  // Deliberately NOT "is #same_address checked": the checkbox and the billing form's
  // visibility are set from different places (the checkbox is hardcoded checked in
  // some checkout views, while the form's display comes from @same_address), so on
  // those pages the box reads "same as shipping" while a fillable billing form sits
  // right below it. Gating on the checkbox meant a billing address typed there was
  // never verified - the SCP-2053 QA failure. Go by the form instead.
  function billingFormInUse(fieldName) {
    var $div = $('#billing_address_div');
    if ($div.length && !$div.is(':visible')) {
      return false;
    }
    return $.trim($(fieldSelector(fieldName, 'line_one')).val() || '') !== '';
  }

  function formatAddressHtml(address) {
    var $div = $('<div>');
    var lineOne = $('<div>').text(address.line_one || '');
    var cityStateZip = $('<div>').text(
      [address.city, address.state].filter(Boolean).join(', ') + ' ' + (address.zip_code || '')
    );
    $div.append(lineOne);
    if (address.line_two) {
      $div.append($('<div>').text(address.line_two));
    }
    $div.append(cityStateZip);
    return $div.html();
  }

  function showCorrectedView(fieldName, response, onProceed) {
    var $modal = $('#address-verification-modal');

    $('#address-verification-corrected-view').show();
    $('#address-verification-invalid-view').hide();
    $('#address-verification-entered-address').html(formatAddressHtml(response.entered));
    $('#address-verification-recommended-address').html(formatAddressHtml(response.recommended));
    $('#address-verification-choice-recommended').prop('checked', true);

    $('#address-verification-use-recommended-button, #address-verification-use-entered-button, #address-verification-edit-link')
      .show();
    $('#address-verification-edit-button').hide();

    $('#address-verification-use-recommended-button').off('click').on('click', function () {
      writeAddress(fieldName, response.recommended);
      $modal.modal('hide');
      onProceed();
    });
    $('#address-verification-use-entered-button').off('click').on('click', function () {
      $modal.modal('hide');
      onProceed();
    });
  }

  function showInvalidView(response) {
    $('#address-verification-corrected-view').hide();
    $('#address-verification-invalid-view').show();
    $('#address-verification-invalid-address').html(formatAddressHtml(response.entered));

    $('#address-verification-use-recommended-button, #address-verification-use-entered-button, #address-verification-edit-link')
      .hide();
    $('#address-verification-edit-button').show();
  }

  function handleResponse(response, fieldName, label, onProceed) {
    if (response.status === 'incomplete') {
      warn('the endpoint saw an incomplete address for ' + fieldSelector(fieldName, 'line_one') +
           ' - check this page\'s field names if the form was filled in.');
      onProceed();
      return;
    }

    if (response.status === 'bypass' || response.status === 'ok') {
      onProceed();
      return;
    }

    $('#address-verification-address-label, #address-verification-address-label-lower').text(label);

    if (response.status === 'corrected') {
      showCorrectedView(fieldName, response, onProceed);
    } else {
      showInvalidView(response);
    }

    $('#address-verification-modal').modal('show');
  }

  window.AddressVerification = {
    check: function (options) {
      var fieldName = options.fieldName;
      var label = options.label || 'Shipping';
      var onProceed = options.onProceed || function () {};
      var address = readAddress(fieldName);

      if (address.country !== 'US') {
        onProceed();
        return;
      }

      if (!isPopulated(address)) {
        warn('no address fields matched ' + fieldSelector(fieldName, 'line_one') +
             ' - verification skipped.');
        onProceed();
        return;
      }

      $.ajax({
        url: '/addresses/verify',
        type: 'POST',
        dataType: 'json',
        headers: { 'X-CSRF-Token': $('meta[name="csrf-token"]').attr('content') },
        data: { address: address },
        success: function (response) {
          handleResponse(response, fieldName, label, onProceed);
        },
        error: function () {
          // Fail open: don't block checkout if the verification endpoint errors out.
          onProceed();
        }
      });
    },

    // Records that `$button` has cleared verification, and un-records it as soon as any
    // of the named address fields changes.
    //
    // The flag has to be cleared, not just set: a re-clicked submit can still be stopped
    // by the browser over an unrelated required field, and anything the user edits while
    // fixing that would otherwise ride through the "already verified" early return on
    // their next click without being checked again.
    //
    // `fieldNames` is one fieldName mapping or an array of them - pass every address on
    // the page (shipping *and* billing) so an edit to either one re-arms verification.
    markVerified: function ($button, fieldNames) {
      $button.data('address-verified', true);

      var maps = $.isArray(fieldNames) ? fieldNames : [fieldNames];
      var selectors = [];
      maps.forEach(function (fieldName) {
        FIELDS.concat(['country']).forEach(function (field) {
          selectors.push(fieldSelector(fieldName, field));
        });
      });

      $(selectors.join(', '))
        .off('.addressVerification')
        .on('input.addressVerification change.addressVerification', function () {
          $button.data('address-verified', false);
        });
    },

    // check() for a billing address, skipping pages/states where no billing address
    // is being entered. Takes the same options as check().
    checkBilling: function (options) {
      if (!billingFormInUse(options.fieldName)) {
        (options.onProceed || function () {})();
        return;
      }

      window.AddressVerification.check(options);
    }
  };
})();
